import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuditoriaAccesos } from '../services/auditoriaAccesosService';
import type { TipoAccionAuditoria } from '../types';

/**
 * Hook que rastrea automáticamente las páginas visitadas por el usuario
 * autenticado y expone funciones para registrar acciones manuales.
 *
 * Debe montarse una sola vez en el componente raíz (App.tsx).
 */
export function useAuditoriaAccesos() {
  const { user, role } = useAuth();
  const location = useLocation();
  const prevPathRef = useRef<string>('');

  // ── Auto-tracking de navegación ─────────────────────────────
  useEffect(() => {
    if (!user?.uid || !role) return;
    // Evitar registrar la misma ruta consecutivamente (ej. re-renders)
    if (location.pathname === prevPathRef.current) return;
    prevPathRef.current = location.pathname;

    const email = user.email || user.uid;

    void AuditoriaAccesos.pageView(
      user.uid,
      email,
      role,
      location.pathname,
    );
  }, [location.pathname, user, role]);

  // ── Registrar acción manual ─────────────────────────────────
  const registrarAccion = useCallback(
    (
      accion: TipoAccionAuditoria,
      descripcion: string,
      detalles?: Record<string, unknown>,
    ) => {
      if (!user?.uid || !role) return;
      const email = user.email || user.uid;
      void AuditoriaAccesos.accionAdmin(
        user.uid,
        email,
        role,
        accion,
        location.pathname,
        descripcion,
        detalles,
      );
    },
    [user, role, location.pathname],
  );

  return { registrarAccion };
}
